import type { TrainingSession } from "@/components/trainingplan/types";
import logoMedium from "@/assets/logo_medium.png";

const BRAND_COLOR = "#007785";
const BRAND_SOFT = "#E6F4F6";
const BRAND_BORDER = "#B9E1E6";

interface PlanPDFTemplateProps {
  planName: string;
  patientName: string;
  selectedObjectives: string[];
  daysPerWeek: number;
  minutesPerSession: number;
  items: TrainingSession[];
  professionalName?: string;
}

const toExercise = (raw: Record<string, unknown>) => ({
  name:
    (raw.name as string) ||
    (raw.exercise as string) ||
    (raw.exercicio as string) ||
    (raw.title as string) ||
    "",
  sets: (raw.sets as number | string) ?? (raw.series as number | string) ?? "",
  reps: (raw.reps as string | number) ?? (raw.repeticoes as string | number) ?? "",
  load: (raw.load as string) ?? (raw.carga as string) ?? (raw.weight as string) ?? "",
  rest: (raw.rest as string) ?? (raw.descanso as string) ?? "",
  notes: (raw.notes as string) ?? (raw.obs as string) ?? "",
});

export function PlanPDFTemplate({
  planName,
  patientName,
  selectedObjectives,
  daysPerWeek,
  minutesPerSession,
  items,
  professionalName,
}: PlanPDFTemplateProps) {
  const weeksMap = new Map<number, TrainingSession[]>();
  items.forEach((it) => {
    const arr = weeksMap.get(it.week_number) || [];
    arr.push(it);
    weeksMap.set(it.week_number, arr);
  });
  const weeks = Array.from(weeksMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([w, s]) => ({
      week: w,
      sessions: s.sort((a, b) => a.display_order - b.display_order),
    }));

  const today = new Date().toLocaleDateString("pt-BR");

  return (
    <div
      style={{
        background: "#ffffff",
        color: "#0f172a",
        padding: "32px",
        fontFamily: "Inter, -apple-system, sans-serif",
        fontSize: "12px",
        lineHeight: 1.5,
        width: "794px",
        maxWidth: "794px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Cover */}
      <div style={{ borderBottom: `2px solid ${BRAND_COLOR}`, paddingBottom: "16px", marginBottom: "20px" }}>
        <div style={{ marginBottom: "8px" }}>
          <img
            src={logoMedium}
            alt="Elinia"
            style={{ height: "41px", width: "auto", display: "block" }}
          />
        </div>
        <h1 style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0 4px" }}>
          {planName || "Plano de treino"}
        </h1>
        <div style={{ fontSize: "13px", color: "#475569" }}>
          Paciente: <strong style={{ color: "#0f172a" }}>{patientName}</strong>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <div style={{ background: "#f1f5f9", padding: "12px 14px", borderRadius: "8px" }}>
          <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase" }}>
            Frequência
          </div>
          <div style={{ fontSize: "16px", fontWeight: 600, marginTop: "4px" }}>
            {daysPerWeek}× por semana
          </div>
        </div>
        <div style={{ background: "#f1f5f9", padding: "12px 14px", borderRadius: "8px" }}>
          <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase" }}>
            Duração por sessão
          </div>
          <div style={{ fontSize: "16px", fontWeight: 600, marginTop: "4px" }}>
            {minutesPerSession} min
          </div>
        </div>
      </div>

      {selectedObjectives.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", marginBottom: "6px" }}
          >
            Objetivos
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {selectedObjectives.map((o) => (
              <span
                key={o}
                style={{
                  background: BRAND_COLOR,
                  color: "#ffffff",
                  padding: "3px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                }}
              >
                {o}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Weeks */}
      {weeks.map((w) => (
        <div key={w.week} style={{ marginBottom: "24px" }}>
          <div
            style={{
              background: BRAND_COLOR,
              color: "#ffffff",
              padding: "8px 12px",
              borderRadius: "6px 6px 0 0",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Semana {w.week}
          </div>
          {w.sessions.map((session, idx) => (
            <div
              key={session.id || idx}
              style={{
                border: `1px solid ${BRAND_BORDER}`,
                borderTop: idx === 0 ? "none" : `1px solid ${BRAND_BORDER}`,
                padding: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: "8px",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "13px" }}>
                  {session.day_of_week} · {session.session_title}
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  {session.duration_minutes ?? "-"} min
                  {session.intensity ? ` · ${session.intensity}` : ""}
                </div>
              </div>
              {(session.exercises || []).length > 0 ? (
                <table
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "48%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: BRAND_SOFT }}>
                      <th style={thStyle}>Exercício</th>
                      <th style={thStyle}>Séries</th>
                      <th style={thStyle}>Reps</th>
                      <th style={thStyle}>Carga</th>
                      <th style={thStyle}>Descanso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(session.exercises || []).map((rawEx, exIdx) => {
                      const ex = toExercise(rawEx);
                      return (
                        <tr key={exIdx}>
                          <td style={{ ...tdStyle, wordBreak: "break-word" }}>{ex.name || "—"}</td>
                          <td style={tdStyleCompact}>{String(ex.sets || "—")}</td>
                          <td style={tdStyleCompact}>{String(ex.reps || "—")}</td>
                          <td style={tdStyleCompact}>{ex.load || "—"}</td>
                          <td style={tdStyleCompact}>{ex.rest || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: "11px", color: "#94a3b8" }}>Sem exercícios cadastrados</div>
              )}
              {session.notes && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "11px",
                    color: "#475569",
                    fontStyle: "italic",
                  }}
                >
                  {session.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {/* Footer */}
      <div
        style={{
          marginTop: "32px",
          paddingTop: "12px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          fontSize: "10px",
          color: "#94a3b8",
        }}
      >
        <span>Gerado em {today}</span>
        {professionalName && <span>Profissional: {professionalName}</span>}
        <span>elinia.ai</span>
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: `1px solid ${BRAND_BORDER}`,
  fontSize: "10px",
  textTransform: "uppercase",
  color: BRAND_COLOR,
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: `1px solid ${BRAND_SOFT}`,
};

const tdStyleCompact: React.CSSProperties = {
  ...tdStyle,
  whiteSpace: "nowrap",
};
